import { cn, memoClassNames } from '@/utils';
import { QuizPlayerProgress } from './quiz-player-progress';
import { QuizPlayerQuestion } from './quiz-player-question';
import { QuizPlayerControls } from './quiz-player-controls';
import { useQuizPlayer, UseQuizPlayerProps } from './use-quiz-player';
import {
  QuizPlayerAttempt,
  QuizPlayerControlsProps,
  QuizPlayerEndProps,
  QuizPlayerProgressProps,
  QuizPlayerQuestionProps,
  QuizPlayerSlots,
  QuizPlayerWelcomeProps,
} from './types';
import { cva, VariantProps } from 'class-variance-authority';
import { QuizPlayerWelcomeScreen } from './screens/quiz-player-welcome';
import { QuizPlayerEndScreen } from './screens/quiz-player-end';

const classes = memoClassNames('QuizPlayer', [
  'root',
  'progress',
  'question',
  'controls',
]);

const rootVariants = cva(
  'mm:w-full mm:flex mm:flex-col mm:gap-6 mm:text-card-foreground mm:relative',
  {
    variants: {
      variant: {
        default:
          'mm:*:data-question:rounded-xl mm:*:data-question:bg-card mm:*:data-question:shadow-xl mm:*:data-question:p-6',
        contained: 'mm:rounded-xl mm:bg-card mm:shadow-xl mm:p-6',
        ghost: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type QuizPlayerRootProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  attempts?: QuizPlayerAttempt[];
  variant?: 'default' | 'contained' | 'ghost';
  slots?: QuizPlayerSlots;
  slotProps?: {
    welcome?: Partial<QuizPlayerWelcomeProps>;
    end?: Partial<QuizPlayerEndProps>;
  };
  defaultScreen?: 'welcome' | 'quiz';
  onFeedback?: (value: number) => void;
};

export type QuizPlayerProps = React.ComponentProps<'div'> &
  UseQuizPlayerProps &
  VariantProps<typeof rootVariants> &
  QuizPlayerRootProps;

export function QuizPlayer({
  // controller props
  questions = [],
  events = {},
  validate,
  requireConfirm = false,
  displayIndex,
  defaultScreen,
  // own props
  title,
  description,
  attempts = [],
  variant = 'default',
  slots = {},
  slotProps = {},
  className,
  onFeedback,
  ...others
}: QuizPlayerProps) {
  const {
    currentQuestion,
    currentIndex,
    selectedAnswer,
    isAnswerValidated,
    isCorrect,
    score,
    screen,
    stats,
    handleAnswerSelection,
    handleConfirmAnswer,
    handleNext,
    handleTimeUp,
    handleStart,
    handleRestart,
    handleCancel,
    isTimerActive,
  } = useQuizPlayer({
    questions,
    events,
    attempts,
    validate,
    requireConfirm,
    defaultScreen,
  });

  const {
    WelcomeScreen = QuizPlayerWelcomeScreen as React.ElementType<QuizPlayerWelcomeProps>,
    EndScreen = QuizPlayerEndScreen as React.ElementType<QuizPlayerEndProps>,
    Progress = QuizPlayerProgress as React.ElementType<QuizPlayerProgressProps>,
    Question = QuizPlayerQuestion as React.ElementType<QuizPlayerQuestionProps>,
    Controls = QuizPlayerControls as React.ElementType<QuizPlayerControlsProps>,
  } = slots;

  const rootProps = {
    ...others,
    className: cn(classes.root, rootVariants({ variant, className })),
    'data-variant': variant,
  };

  if (screen === 'welcome') {
    return (
      <div {...rootProps}>
        <WelcomeScreen
          {...slotProps.welcome}
          title={title}
          description={description}
          questions={questions}
          onStart={handleStart}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  if (screen === 'end') {
    return (
      <div {...rootProps}>
        <EndScreen
          {...slotProps.end}
          score={score}
          totalQuestions={questions.length}
          stats={stats}
          onRestart={handleRestart}
          onFeedback={onFeedback}
        />
      </div>
    );
  }

  return (
    <div {...rootProps}>
      <Progress
        className={classes.progress}
        title={title}
        current={currentIndex + 1}
        total={questions.length}
        timeLimit={currentQuestion?.timeLimit}
        isTimerActive={isTimerActive}
        onTimeUp={handleTimeUp}
        onCancel={handleCancel}
      />

      {currentQuestion && (
        <Question
          title={title}
          className={classes.question}
          question={currentQuestion.question}
          choices={currentQuestion.choices}
          selectedAnswer={selectedAnswer}
          isAnswerValidated={isAnswerValidated}
          correctAnswer={currentQuestion.correctAnswer}
          hint={currentQuestion.hint}
          onSelectAnswer={handleAnswerSelection}
          isCorrect={isCorrect}
          data-question={currentQuestion.id}
        />
      )}

      <Controls
        className={classes.controls}
        onCancel={handleCancel}
        onConfirm={handleConfirmAnswer}
        onNext={handleNext}
        selectedAnswer={selectedAnswer}
        isAnswerValidated={isAnswerValidated}
        isLastQuestion={currentIndex === questions.length - 1}
        requireConfirm={requireConfirm}
      />
    </div>
  );
}
